// src/store/slices/interviewApplicationSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import interviewApi from '../../api/interviewApi';

export const fetchInterviewApplications = createAsyncThunk(
  'interviewApplication/fetchInterviewApplications',
  async ({ page = 1, page_size = 10, search = '' }, { rejectWithValue }) => {
    try {
      const response = await interviewApi.getInterviewApplications({ page, page_size, search });
      return response; // full response with data + pagination
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to fetch applications');
    }
  }
);

export const createInterviewApplication = createAsyncThunk(
  'interviewApplication/createInterviewApplication',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await interviewApi.createInterviewApplication(formData);
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || 'Failed to create application');
    }
  }
);

export const submitPublicInterviewForm = createAsyncThunk(
  'interviewApplication/submitPublicInterviewForm',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await interviewApi.submitPublicInterviewForm(formData);
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data.resume || 'Failed to create application');
    }
  }
);

export const updateInterviewApplication = createAsyncThunk(
  'interviewApplication/updateInterviewApplication',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await interviewApi.updateInterviewApplication(id, data);
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || 'Failed to update application');
    }
  }
);

export const deleteInterviewApplication = createAsyncThunk(
  'interviewApplication/deleteInterviewApplication',
  async (id, { rejectWithValue }) => {
    try {
      await interviewApi.deleteInterviewApplication(id);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to delete application');
    }
  }
);

export const approveInterviewApplication = createAsyncThunk(
  'interviewApplication/approveInterviewApplication',
  async ({ id, remarks = '' }, { rejectWithValue }) => {
    try {
      const response = await interviewApi.approveInterviewApplication(id, remarks);
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || 'Failed to approve application');
    }
  }
);

export const rejectInterviewApplication = createAsyncThunk(
  'interviewApplication/rejectInterviewApplication',
  async ({ id, remarks }, { rejectWithValue }) => {
    try {
      const response = await interviewApi.rejectInterviewApplication(id, remarks);
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || 'Failed to reject application');
    }
  }
);

const interviewApplicationSlice = createSlice({
  name: 'interviewApplication',
  initialState: {
    applications: [],
    pagination: {
      count: 0,
      current_page: 1,
      total_pages: 1,
      page_size: 10,
    },
    isLoading: false,
    error: null,
  },
  reducers: {
    clearInterviewApplicationError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch list
      .addCase(fetchInterviewApplications.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchInterviewApplications.fulfilled, (state, action) => {
        state.isLoading = false;
        state.applications = action.payload?.data || [];
        state.pagination = action.payload?.pagination || state.pagination;
      })
      .addCase(fetchInterviewApplications.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Create
      .addCase(createInterviewApplication.fulfilled, (state, action) => {
        state.applications.unshift(action.payload?.data || action.payload);
        state.pagination.count += 1;
      })

       .addCase(submitPublicInterviewForm.fulfilled, (state, action) => {
        state.applications.unshift(action.payload?.data || action.payload);
        state.pagination.count += 1;
      })

      // Update
      .addCase(updateInterviewApplication.fulfilled, (state, action) => {
        const updated = action.payload?.data || action.payload;
        const index = state.applications.findIndex(a => a.id === updated.id);
        if (index !== -1) {
          state.applications[index] = updated;
        }
      })

      // Delete
      .addCase(deleteInterviewApplication.fulfilled, (state, action) => {
        state.applications = state.applications.filter(a => a.id !== action.payload);
        state.pagination.count -= 1;
      })

      // Approve / Reject (both update status)
      .addCase(approveInterviewApplication.fulfilled, (state, action) => {
        const updated = action.payload?.data || action.payload;
        const index = state.applications.findIndex(a => a.id === updated.id);
        if (index !== -1) {
          state.applications[index] = updated;
        }
      })
      .addCase(rejectInterviewApplication.fulfilled, (state, action) => {
        const updated = action.payload?.data || action.payload;
        const index = state.applications.findIndex(a => a.id === updated.id);
        if (index !== -1) {
          state.applications[index] = updated;
        }
      });
  },
});

export const { clearInterviewApplicationError } = interviewApplicationSlice.actions;
export default interviewApplicationSlice.reducer;